import princeton from "@/assets/nj-communities/princeton.jpg";
import westWindsor from "@/assets/nj-communities/west-windsor.jpg";
import hamilton from "@/assets/nj-communities/hamilton.jpg";
import robbinsville from "@/assets/nj-communities/robbinsville.jpg";
import lawrenceville from "@/assets/nj-communities/lawrenceville.jpg";
import trenton from "@/assets/nj-communities/trenton.jpg";
import monmouth from "@/assets/nj-communities/monmouth-county.jpg";
import ocean from "@/assets/nj-communities/ocean-county.webp";
import middlesex from "@/assets/nj-communities/middlesex-county.jpg";

export interface Community {
  slug: string;
  name: string;
  caption: string;
  county: string;
  image: string;
  alt: string;
  /** Short teaser shown in hover preview */
  preview: string;
  /** Long intro paragraph on the detail page */
  intro: string;
  highlights: string[];
  lifestyle: string;
  marketNote: string;
  stats: { label: string; value: string }[];
}

export const njCommunities: Community[] = [
  {
    slug: "princeton",
    name: "Princeton",
    caption: "Ivy & Heritage",
    county: "Mercer County",
    image: princeton,
    alt: "Historic Princeton New Jersey downtown at golden hour",
    preview:
      "Ivy-clad architecture, walkable downtown, and one of the most prestigious school districts in the country.",
    intro:
      "Anchored by Princeton University, Princeton offers a rare blend of intellectual energy, historic architecture, and quiet residential streets. Nassau Street's bookshops, cafés, and galleries give the town a European feel, while surrounding neighborhoods range from centuries-old estates to modern custom builds.",
    highlights: [
      "Nationally ranked Princeton Public Schools",
      "Walkable downtown along Nassau Street",
      "Historic districts with pre-1900 homes",
      "McCarter Theatre, art museum, and lecture series",
      "Easy NJ Transit access to NYC and Philadelphia",
    ],
    lifestyle:
      "Weekend farmers' markets on Hinds Plaza, canal-side runs along the Delaware & Raritan towpath, and Sunday brunch at agricola. Princeton attracts families, academics, and professionals who want a small-town rhythm with big-city cultural depth.",
    marketNote:
      "Princeton commands some of Central New Jersey's strongest per-square-foot values, especially in the western section and historic downtown. Inventory is tight — the right buyer strategy matters.",
    stats: [
      { label: "County", value: "Mercer" },
      { label: "Vibe", value: "Historic · Academic" },
      { label: "Commute to NYC", value: "~60 min rail" },
    ],
  },
  {
    slug: "west-windsor",
    name: "West Windsor",
    caption: "Estate Living",
    county: "Mercer County",
    image: westWindsor,
    alt: "West Windsor New Jersey colonial luxury home in autumn",
    preview:
      "Top-rated schools, spacious estate neighborhoods, and the Princeton Junction train station to Manhattan.",
    intro:
      "West Windsor is the Central Jersey benchmark for family-focused suburban living. Estate-sized lots, planned communities, and the West Windsor-Plainsboro Regional School District draw relocating executives and long-term families alike.",
    highlights: [
      "West Windsor-Plainsboro Regional Schools (consistently top-ranked)",
      "Princeton Junction NJ Transit — direct to NYC Penn Station",
      "Mercer County Park & Lake Mercer",
      "Grover's Mill, Village at Grover's Mill, and Windsor Meadows",
      "Diverse dining along Route 571",
    ],
    lifestyle:
      "Youth sports leagues, community pools, weekend rowing at Lake Mercer, and one of the most diverse suburban populations in the state. Ideal for buyers prioritizing schools plus a manageable NYC commute.",
    marketNote:
      "Colonial and transitional homes on quarter- to full-acre lots dominate. Newer construction sits at a premium; established neighborhoods offer strong long-term appreciation.",
    stats: [
      { label: "County", value: "Mercer" },
      { label: "Vibe", value: "Family · Executive" },
      { label: "Commute to NYC", value: "~55 min rail" },
    ],
  },
  {
    slug: "hamilton",
    name: "Hamilton",
    caption: "Classic Americana",
    county: "Mercer County",
    image: hamilton,
    alt: "Hamilton Township New Jersey brick colonial home",
    preview:
      "Approachable price points, mature neighborhoods, and Grounds For Sculpture on your doorstep.",
    intro:
      "Hamilton Township is one of Central Jersey's most livable value plays — established neighborhoods, solid schools, and a diversity of housing stock from starter capes to substantial colonials. Downtown Hamilton, Yardville, and Mercerville each carry their own character.",
    highlights: [
      "Hamilton Train Station — Northeast Corridor service",
      "Grounds For Sculpture — 42-acre art park",
      "Sayen Gardens seasonal festivals",
      "Diverse neighborhoods across a large township",
      "Convenient to Trenton, Princeton, and I-295",
    ],
    lifestyle:
      "Family-friendly parks, Little League fields, Italian bakeries, and one of the best BBQ scenes in the region. A great fit for first-time buyers, growing families, and downsizers.",
    marketNote:
      "Hamilton offers the widest price band in Mercer County — from entry-level homes under $400K to custom builds well past $1M. Neighborhood-level knowledge is essential here.",
    stats: [
      { label: "County", value: "Mercer" },
      { label: "Vibe", value: "Suburban · Family" },
      { label: "Commute to NYC", value: "~65 min rail" },
    ],
  },
  {
    slug: "robbinsville",
    name: "Robbinsville",
    caption: "Town Center Charm",
    county: "Mercer County",
    image: robbinsville,
    alt: "Robbinsville New Jersey town center at twilight",
    preview:
      "A planned town center, top-tier schools, and one of the fastest-growing communities in Mercer County.",
    intro:
      "Robbinsville has quietly become one of Central Jersey's most in-demand destinations. Its Washington Town Center — a walkable mix of restaurants, retail, and residential — anchors a community that feels both modern and connected.",
    highlights: [
      "Washington Town Center — restaurants, shops, lakeside walking loop",
      "Robbinsville Public Schools — highly ranked",
      "Easy access to NJ Turnpike Exit 7A",
      "Newer construction throughout the township",
      "BAPS Shri Swaminarayan Mandir — a regional landmark",
    ],
    lifestyle:
      "Outdoor concerts at the Town Center gazebo, weekend farmers' markets, and family-focused parks. Robbinsville draws professionals who want new construction, walkability, and quick access to the Turnpike.",
    marketNote:
      "Demand consistently outpaces supply. Town Center townhomes and single-family homes in Foxmoor, Windsor Regency, and Town Center trade quickly.",
    stats: [
      { label: "County", value: "Mercer" },
      { label: "Vibe", value: "Modern · Walkable" },
      { label: "Commute to NYC", value: "~70 min" },
    ],
  },
  {
    slug: "lawrenceville",
    name: "Lawrenceville",
    caption: "Historic Main Street",
    county: "Mercer County",
    image: lawrenceville,
    alt: "Lawrenceville New Jersey historic main street with elm trees",
    preview:
      "A postcard main street, the Lawrenceville School, and easy access to both Princeton and Trenton.",
    intro:
      "Lawrenceville pairs a genuinely historic Main Street with mature residential neighborhoods and a strong school tradition. The Lawrenceville School and Rider University give the town an academic backbone; the shops and restaurants along Route 206 give it daily rhythm.",
    highlights: [
      "The Lawrenceville School (private) & Rider University",
      "Historic Main Street district",
      "Lawrence Hopewell Trail — 20+ miles of paved paths",
      "Central to Princeton, Trenton, and Hamilton",
      "Mix of colonial, ranch, and new construction",
    ],
    lifestyle:
      "Independent bookstores, historic bed-and-breakfasts, and community events year-round. Popular with academics, medical professionals, and families who want proximity to Princeton at a friendlier price.",
    marketNote:
      "Homes near the historic core and the Hopewell Trail hold value particularly well. Newer developments off Princeton Pike offer turn-key options.",
    stats: [
      { label: "County", value: "Mercer" },
      { label: "Vibe", value: "Historic · Academic" },
      { label: "Commute to NYC", value: "~65 min rail" },
    ],
  },
  {
    slug: "trenton",
    name: "Trenton",
    caption: "Capital Skyline",
    county: "Mercer County",
    image: trenton,
    alt: "Trenton New Jersey skyline with State House gold dome at dusk",
    preview:
      "New Jersey's capital — historic architecture, waterfront revitalization, and strong investor upside.",
    intro:
      "As the state capital, Trenton offers historic neighborhoods, riverfront revitalization, and some of the most compelling investor opportunities in the region. Mill Hill, Berkeley Square, and the Villa Park district each carry distinct architectural character.",
    highlights: [
      "New Jersey State House and government district",
      "Mill Hill Historic District — brownstones and rowhomes",
      "Trenton Transit Center — Amtrak & NJ Transit",
      "Waterfront redevelopment along the Delaware",
      "Trenton Thunder baseball at Trenton Thunder Ballpark",
    ],
    lifestyle:
      "A city of neighborhoods with genuine history. Ideal for investors, first-time buyers priced out of surrounding towns, and buyers who want walk-to-transit convenience.",
    marketNote:
      "Sharp value differentials by block — local knowledge is critical. Rehab and rental opportunities can produce meaningful returns for the right buyer.",
    stats: [
      { label: "County", value: "Mercer" },
      { label: "Vibe", value: "Urban · Historic" },
      { label: "Commute to NYC", value: "~55 min Amtrak" },
    ],
  },
  {
    slug: "monmouth-county",
    name: "Monmouth County",
    caption: "Atlantic Shores",
    county: "Monmouth County",
    image: monmouth,
    alt: "Monmouth County New Jersey shingle-style coastal luxury home",
    preview:
      "Shore-town living from Rumson to Spring Lake — coastal luxury, top schools, and Manhattan ferries.",
    intro:
      "Monmouth County covers the northern Jersey Shore and its adjacent inland suburbs — Rumson, Fair Haven, Red Bank, Spring Lake, Manasquan, Middletown, Colts Neck, and more. Each town has a distinct personality; together they offer some of the strongest coastal real estate in the Northeast.",
    highlights: [
      "SeaStreak & NY Waterway ferries to Manhattan (~45 min)",
      "Red Bank cultural scene — Count Basie Center, dining",
      "Spring Lake boardwalk & Victorian architecture",
      "Sandy Hook, Monmouth Park, and Two River Theater",
      "Consistently top-ranked school districts",
    ],
    lifestyle:
      "Beach mornings, sailboat afternoons, downtown dinners in Red Bank. Popular with NYC commuters via ferry, retirees, second-home buyers, and families wanting coastal lifestyle year-round.",
    marketNote:
      "Coastal towns command a premium; inland Monmouth (Colts Neck, Holmdel, Marlboro) offers estate living at more measured prices. Seasonality matters — timing is a real strategy.",
    stats: [
      { label: "Region", value: "North Shore" },
      { label: "Vibe", value: "Coastal · Refined" },
      { label: "Commute to NYC", value: "~45 min ferry" },
    ],
  },
  {
    slug: "ocean-county",
    name: "Ocean County",
    caption: "Jersey Shore",
    county: "Ocean County",
    image: ocean,
    alt: "Ocean County New Jersey boardwalk and Atlantic ocean",
    preview:
      "Classic Jersey Shore — LBI, Point Pleasant, Bay Head, and the state's most active second-home market.",
    intro:
      "Ocean County stretches from Point Pleasant Beach south through Long Beach Island and beyond — a wide range of shore towns, bayfront communities, and inland retirement enclaves. This is where many New Jersey families spend their summers, and increasingly, their retirements.",
    highlights: [
      "Long Beach Island (LBI) — Barnegat Light to Holgate",
      "Point Pleasant, Bay Head, Mantoloking, Lavallette",
      "Barnegat Bay boating & Island Beach State Park",
      "Retirement-friendly communities (Toms River, Manchester)",
      "Strong short-term rental market for investors",
    ],
    lifestyle:
      "Boardwalks, boats, bay sunsets, and beach-town breakfasts. Excellent for second-home buyers, retirees, and investors targeting the summer rental market.",
    marketNote:
      "LBI and oceanfront command significant premiums; bayside and inland Ocean County offer strong value. Flood zones, insurance, and elevation certificates need experienced guidance.",
    stats: [
      { label: "Region", value: "South Shore" },
      { label: "Vibe", value: "Coastal · Relaxed" },
      { label: "Second homes", value: "Highest in state" },
    ],
  },
  {
    slug: "middlesex-county",
    name: "Middlesex County",
    caption: "Storybook Streets",
    county: "Middlesex County",
    image: middlesex,
    alt: "Middlesex County New Jersey leafy suburban street in autumn",
    preview:
      "Cranbury, Plainsboro, East Windsor, Spotswood — commuter-friendly towns with strong schools and diverse housing.",
    intro:
      "Middlesex County covers a broad swath of Central New Jersey, from Cranbury and Plainsboro in the west to Old Bridge and Sayreville in the east. It's one of the most diverse and commuter-friendly counties in the state, with quick access to the NJ Turnpike, Route 1, and multiple rail lines.",
    highlights: [
      "Cranbury Village — one of the state's best-preserved colonial downtowns",
      "Plainsboro — WW-P school district, Princeton Medical Center",
      "East Windsor & Hightstown — value-focused family towns",
      "Spotswood — home of John's brokerage office",
      "Rutgers University in New Brunswick",
    ],
    lifestyle:
      "Weekend antiques in Cranbury, community events in East Windsor, and easy access to both NYC and Philadelphia. A strong fit for commuters, families, and multi-generational households.",
    marketNote:
      "Middlesex offers exceptional value relative to Mercer County, especially in Plainsboro and East Windsor. Cranbury remains one of the tightest, most desirable micro-markets in the region.",
    stats: [
      { label: "County", value: "Middlesex" },
      { label: "Vibe", value: "Diverse · Commuter" },
      { label: "Commute to NYC", value: "~50–65 min" },
    ],
  },
];

export const getCommunityBySlug = (slug: string) =>
  njCommunities.find((c) => c.slug === slug);
